import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { MantineProvider, ColorSchemeScript, Modal, mergeThemeOverrides, rem } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import '@mantine/core/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/spotlight/styles.css';
import './styles/print.css';
import './styles/ui-prefs.css';
import { router } from './router';
import { theme } from './theme';
import { initObservability } from './lib/observability';
import { reloadForFreshChunks } from './lib/chunkReload';
import { PwaUpdater } from './components/PwaUpdater';
import { useUi, systemColorScheme } from './lib/uiStore';

/**
 * Modal size overrides — caps the largest modal at 1000px so floor-shell
 * (1280px) and compact-laptop users never see a modal that bumps the
 * viewport edges. The default Mantine size scale is biased toward marketing
 * pages; staff-facing modals (CSV import, location switcher, edit forms)
 * read better at these tighter widths. We map the named sizes via styles
 * because Mantine's Modal exposes the size as a CSS var (`--modal-size`).
 */
const SIZE_MAP: Record<string, string> = {
  sm: rem(480),
  md: rem(640),
  lg: rem(800),
  xl: rem(1000),
};

const themeWithModalSizes = mergeThemeOverrides(theme, {
  components: {
    Modal: Modal.extend({
      defaultProps: {
        size: 'md',
      },
      styles: (_t, props) => {
        const size = typeof props.size === 'string' ? SIZE_MAP[props.size] : undefined;
        return size ? { content: { maxWidth: size } } : {};
      },
    }),
  },
});

initObservability();

// Recover from stale code-split chunks after a deploy. A tab loaded against a
// previous build holds references to content-hashed chunk filenames that the
// new build has deleted; importing one then fails (the SPA fallback serves
// index.html with a text/html MIME where a module was expected). Vite raises
// `vite:preloadError` for that failed dynamic import — reload once to fetch the
// fresh build. The reload is time-guarded inside reloadForFreshChunks so a
// genuinely broken deploy can't trap the user in a loop. See lib/chunkReload.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  reloadForFreshChunks();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root element in index.html');

// eslint-disable-next-line react-refresh/only-export-components -- entry-point file; App must live here so the provider stack mounts inside createRoot.render
function App() {
  const colorScheme = useUi((s) => s.colorScheme);
  const themePreference = useUi((s) => s.themePreference);
  const reduceMotion = useUi((s) => s.reduceMotion);

  // When the user picks "System", keep the applied scheme in lock-step with the
  // OS: seed it once and re-resolve whenever `prefers-color-scheme` flips.
  useEffect(() => {
    if (themePreference !== 'auto') return;
    const mql = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mql) return;
    const sync = () => useUi.getState().setColorScheme(systemColorScheme());
    sync();
    mql.addEventListener('change', sync);
    return () => mql.removeEventListener('change', sync);
  }, [themePreference]);

  // Mirror the reduce-motion preference onto <html> so styles/ui-prefs.css can
  // collapse animations app-wide.
  useEffect(() => {
    document.documentElement.dataset.reduceMotion = String(reduceMotion);
  }, [reduceMotion]);

  return (
    <MantineProvider theme={themeWithModalSizes} forceColorScheme={colorScheme}>
      <Notifications />
      <PwaUpdater />
      <ModalsProvider>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </ModalsProvider>
    </MantineProvider>
  );
}

createRoot(root).render(
  <StrictMode>
    <ColorSchemeScript defaultColorScheme="light" />
    <App />
  </StrictMode>,
);
